import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import { AdventDoor } from "apps/user/learner/api/types";
import {
  DoorMechanicResponse,
  formatCompletedDate,
} from "apps/user/learner/lib/advent-door-mechanics";
import { useTranslation } from "lib/i18n/use-translation";
import { logError } from "utility/Function";

import styles from "../../december-doors.module.scss";

export type CompleteDoor = (
  response: DoorMechanicResponse | null
) => Promise<void>;

export interface MechanicProps {
  door: AdventDoor;
  onComplete: CompleteDoor;
}

/**
 * Sends a completion and tracks whether it is in flight or failed; a failure
 * is logged and shown inline so the learner can try again. `submit` keeps
 * its identity across renders, so a running timer that depends on it is not
 * restarted when the page re-renders.
 */
export const useCompletion = (onComplete: CompleteDoor) => {
  const [status, setStatus] = useState<"idle" | "saving" | "failed">("idle");
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const submit = useCallback(
    async (response: DoorMechanicResponse | null): Promise<void> => {
      setStatus("saving");
      try {
        await onCompleteRef.current(response);
      } catch (error) {
        logError("Failed to complete advent door", error);
        setStatus("failed");
        return;
      }
      setStatus("idle");
    },
    []
  );

  return {
    submit,
    isSaving: status === "saving",
    hasFailed: status === "failed",
  };
};

export const CompletionError: FC<{ show: boolean }> = ({ show }) => {
  const { t } = useTranslation(["decemberDoors"]);
  if (!show) return null;
  return (
    <p className={styles["mechanic-error"]} role="alert">
      {t("decemberDoors:error.body")}
    </p>
  );
};

/** "Done on 12 December", once the door has a completion. */
export const CompletedOn: FC<{ door: AdventDoor }> = ({ door }) => {
  const { t, i18n } = useTranslation(["decemberDoors"]);
  if (!door.isCompleted) return null;
  if (!door.completedAt) {
    throw new Error(`Door ${door.dayNumber} is completed with no date`);
  }
  return (
    <p className={styles["mechanic-completed-on"]}>
      {t("decemberDoors:mechanic.completedOn", {
        date: formatCompletedDate(door.completedAt, i18n.language),
      })}
    </p>
  );
};

/** The finished state of a one-shot mechanic: its closing line and date. */
export const CompletedNote: FC<{ door: AdventDoor; line: string }> = ({
  door,
  line,
}) => (
  <div className={styles.mechanic} data-testid="mechanic-completed">
    <p className={styles["mechanic-confirm"]}>{line}</p>
    <CompletedOn door={door} />
  </div>
);
